
import { Component, Vue } from 'vue-property-decorator';
import { ProfileField, defaultProfileFields } from "../../../types/ProfileField";
import { User } from "@api/models/User";
import { UserProfile } from "@api/models/UserProfile";
import { SettingsViewPart } from "@service/components/settings/types/SettingsViewPart";
import { SegmentDataItem } from "@share/common/segment/SegmentData";
import { isEmailValid, isPhoneNumberValid } from '@common/stringValidationHelper';
import { TagsState } from "@service/store/types/TagsState";

import { UserFormBaseInterface } from "@service/components/user-form-base/interfaces/UserFormBaseInterface";
import { InfoBlocksEnum } from '@service/types/InfoBlocksEnum';

@Component({})
export default class UserFormBase extends Vue implements UserFormBaseInterface {
    fields: Array<ProfileField> = defaultProfileFields;
    tags: any[] = [];
    userId = this.$store.state.session.userId;
    user: User = new User({id: this.userId, language: this.lang });
    profile: UserProfile = new UserProfile ({});
    validationMessages: Record<string, string> = {};
    currentViewPart: SettingsViewPart = SettingsViewPart.info;
    valid = true;

    setCurrentPartView(value: SettingsViewPart) {
        this.currentViewPart = value;
    }

    openDeviceTest() {
        this.$emit('toggleFrontBlock', InfoBlocksEnum.deviceTest)
    }

    async getTags() {

        //if userTags not loaded, load its
        if (!this.tagsState.usersTags) {
            await this.$store.dispatch("tags/loadUsersTags");
        }

        const profileParams = this.$store.getters['events/selectedEventViewParam']('profile', null);
        const tagsSettings = profileParams?.tags;

        const tags = this.tagsState.usersTags?.filter((tag) => tag.parentId === tagsSettings?.parentId) || [];

        this.tags = tags.map(tag => {
            return { tag: tag, isSelected: false };
        });
    }

    get needValidateTags() {
        return this.tags.length > 0;
    }

    get tagsState(): TagsState {
        return this.$store.state.tags;
    }

    get segmentData(): SegmentDataItem[] {
        return [{
            active: this.currentViewPart === SettingsViewPart.info,
            title: this.$t("settings." + SettingsViewPart.info).toString(),
            value: SettingsViewPart.info,
            marked: this.fields.some((field) => this.validationMessages[field.name] && ["input", "textarea"].includes(field.type))
        },
        {
            active: this.currentViewPart === SettingsViewPart.interests,
            title: this.$t("settings." + SettingsViewPart.interests).toString(),
            value: SettingsViewPart.interests,
            marked: Object.prototype.hasOwnProperty.call(this.validationMessages, "offer") ||
             Object.prototype.hasOwnProperty.call(this.validationMessages, "looking")
        },
        {
            active: this.currentViewPart === SettingsViewPart.settings,
            title: this.$t("settings." + SettingsViewPart.settings).toString(),
            value: SettingsViewPart.settings
        },
        {
            active: this.currentViewPart === SettingsViewPart.deviceTest,
            title: this.$t("web-conferences." + SettingsViewPart.deviceTest).toString(),
            value: SettingsViewPart.deviceTest
        }];
    }

    get lang() {
        return this.$store.state.session?.language ?? 'en'
    }

    validateBase(tagsRequired = true): boolean {
        let valid = true;

        const tagsIds = this.profile.tagsIds;
        if (!tagsIds?.length && tagsRequired) {
            Vue.set(this.validationMessages, "offer", this.$t("settings.selectSome"));
            valid = false;
        }

        const interestingTagIds = this.profile.interestingTagIds;
        if (!interestingTagIds?.length && tagsRequired) {
            Vue.set(this.validationMessages, "looking", this.$t("settings.selectSome"));
            valid = false;
        }

        const email = this.profile.email;
        if(email && !isEmailValid(email)) {
            Vue.set(this.validationMessages, "email", this.$t("settings.invalidEmail"));
            valid = false;
        }

        const phone = this.profile.phoneNumber;
        if(phone && !isPhoneNumberValid(phone)) {
            Vue.set(this.validationMessages, "phoneNumber", this.$t("settings.invalidPhoneNumber"));
            valid = false;
        }

        this.fields.forEach( field => {
            if(field?.required && !this.profile[field.name]) {
                Vue.set(this.validationMessages, field.name, this.$t("settings.requiredField"));
                valid = false;
            }
        });


        return valid;
    }

    resetForm() {
        this.profile = new UserProfile({});
        this.user = new User({ language: this.lang });
        this.userId = null;
    }

    getProfileParamsAndSetFieldsAndTags() {
        const profileParams = this.$store.getters['events/selectedEventViewParam']('profile', null);

        if (profileParams?.fields) {
            this.fields = profileParams?.fields
        }

        if (profileParams?.tags) {
            this.getTags();
        }

        return profileParams;
    }
}
